"use client";

import { useEffect, useMemo, useState } from "react";
import { getConfig, type PublicRegion } from "@fm/api";
import { getApiBaseUrl } from "../lib/config";
import type { SearchBarDefaultPickup } from "../components/EmbedSearchBar";

/**
 * The widget's own regions and fleet default pickup, learned from its
 * bootstrap endpoint `GET /v1/config` — API-key authenticated exactly like
 * `useBusinessProfile`, which this hook mirrors: same `apiClient`
 * construction, same "no key, no fetch, not loading" and
 * "failure logs and clears, never throws" discipline. No slug and no
 * webpage-settings fetch required.
 *
 * ## `defaultPickup` is read, not derived
 *
 * `/config` sends the fleet's own `default_pickup_*`, so this reads it. It
 * is tempting to derive it instead: a region with no address of its own
 * comes back already resolved down to the fleet's default and marked
 * `pickup_source: "fleet"`, so any such region is carrying the answer. That
 * works until a fleet gives every one of its regions an address, at which
 * point no region reports that source and the fleet's default silently
 * disappears — a correct behaviour resting on an accident of configuration.
 *
 * A fleet that has configured no default at all leaves `defaultPickup`
 * `null`, and "All regions" simply clears the field, exactly as
 * `EmbedSearchBar` documents for an omitted `defaultPickup`.
 *
 * ## Failure discipline
 *
 * The widget is a third-party embed: a slow or failing `/config` must never
 * break the rest of the page. A rejected request is logged and leaves
 * `regions` unset (`undefined`) and `defaultPickup` `null` — the same state
 * a fleet with no regions produces — so the caller renders exactly today's
 * bar rather than an error.
 */
export function useFleetRegions(apiKey: string, apiBaseUrl?: string) {
  const [regions, setRegions] = useState<PublicRegion[] | undefined>(undefined);
  const [defaultPickup, setDefaultPickup] =
    useState<SearchBarDefaultPickup | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // `getConfig` requires a concrete `apiKey: string`, unlike the broader
  // `ApiClient` type (optional `apiKey`, optional `getAuthToken`) other
  // hooks in this file build — this hook's own `apiKey` param is always a
  // string, so its client is typed to match exactly what `getConfig` needs.
  const apiClient = useMemo<{ apiKey: string; baseUrl: string }>(
    () => ({
      apiKey,
      baseUrl: getApiBaseUrl(apiBaseUrl),
    }),
    [apiKey, apiBaseUrl],
  );

  useEffect(() => {
    if (!apiKey) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const config = await getConfig(apiClient);
        if (cancelled) return;

        if (config?.regions?.length) {
          setRegions(config.regions);
        }

        const hasFleetDefault =
          config?.default_pickup_location != null ||
          config?.default_pickup_city != null ||
          config?.default_pickup_lat != null;

        if (hasFleetDefault) {
          setDefaultPickup({
            lat: config.default_pickup_lat ?? null,
            lng: config.default_pickup_lng ?? null,
            city: config.default_pickup_city ?? "",
            state: config.default_pickup_state ?? "",
            locationText: config.default_pickup_location ?? "",
          });
        }
      } catch (error) {
        // No picker rather than an error — see the doc above.
        console.error("Failed to load fleet regions for the widget:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [apiKey, apiClient]);

  return { regions, defaultPickup, isLoading };
}
